import { logger } from "@/services/logger";
import { tauriStoreConversationRepository } from "../tauriStoreConversationRepository";
import { getDb } from "./connection";
import { sqliteConversationRepository } from "./sqliteConversationRepository";

const log = logger.scope("sqlite-migration");
const MIGRATION_FLAG = "migrated_from_tauri_store";

/**
 * One-time migration of Phase 2's `conversations.json` (via
 * `tauriStoreConversationRepository`) into SQLite. Idempotent and
 * re-runnable: it's guarded by a `meta` flag, and every write is an upsert,
 * so a failure partway through (crash, disk full) just means it retries in
 * full next launch rather than double-importing or losing anything. The
 * legacy JSON file is never deleted — pure safety net, per "never lose user
 * data."
 */
export async function migrateFromTauriStoreIfNeeded(): Promise<void> {
  const db = await getDb();
  const rows = await db.select<{ value: string }[]>("SELECT value FROM meta WHERE key = $1", [
    MIGRATION_FLAG,
  ]);
  if (rows.length > 0) return;

  try {
    const summaries = await tauriStoreConversationRepository.list();
    let migrated = 0;
    for (const summary of summaries) {
      const conversation = await tauriStoreConversationRepository.get(summary.id);
      if (!conversation) continue;
      await sqliteConversationRepository.save({
        ...conversation,
        knowledgeScope: conversation.knowledgeScope ?? { mode: "none" },
      });
      migrated++;
    }
    await db.execute("INSERT OR REPLACE INTO meta (key, value) VALUES ($1, $2)", [
      MIGRATION_FLAG,
      String(Date.now()),
    ]);
    if (migrated > 0) {
      log.info(`Migrated ${migrated} conversation(s) from the legacy local store to SQLite.`);
    }
  } catch (err) {
    // Leave the flag unset so this retries on next launch — never mark a
    // failed migration as done.
    log.error("Migration from the legacy conversation store failed; will retry next launch.", err);
  }
}
